import { Purchase, PurchaseFormData, PurchaseWithProduct } from '../types/purchase';
import { Product } from '../types/product';
import { supabase } from '../lib/supabase';
import { uploadImage } from './cloudinaryService';

// Exportar para que sea considerado un módulo
export {};

// Convertir datos de Supabase a formato Purchase
const convertSupabasePurchase = (supabasePurchase: any): Purchase => ({
  id: supabasePurchase.id,
  product_id: supabasePurchase.product_id,
  quantity: supabasePurchase.quantity,
  unit_cost: supabasePurchase.unit_cost,
  shipping_cost: supabasePurchase.shipping_cost,
  other_expenses: supabasePurchase.other_expenses,
  total_cost: supabasePurchase.total_cost,
  final_unit_cost: supabasePurchase.final_unit_cost,
  reference_document: supabasePurchase.reference_document,
  reference_document_url: supabasePurchase.reference_document_url,
  purchase_date: supabasePurchase.purchase_date,
  created_at: supabasePurchase.created_at,
  updated_at: supabasePurchase.updated_at
});

// Convertir datos de Supabase a formato PurchaseWithProduct
const convertSupabasePurchaseWithProduct = (supabasePurchase: any, product: Product): PurchaseWithProduct => ({
  ...convertSupabasePurchase(supabasePurchase),
  product
});

// Servicio de compras con Supabase
export const purchaseService = {
  // Obtener todas las compras
  getPurchases: async (): Promise<PurchaseWithProduct[]> => {
    try {
      const { data, error } = await supabase
        .from('purchases')
        .select(`
          *,
          products:product_id (*)
        `)
        .order('purchase_date', { ascending: false });

      if (error) {
        console.error('Error al obtener compras:', error);
        return [];
      }

      return data.map((item: any) => {
        const product = item.products;
        return convertSupabasePurchaseWithProduct(item, product);
      });
    } catch (error) {
      console.error('Error inesperado al obtener compras:', error);
      return [];
    }
  },

  // Obtener una compra por ID
  getPurchaseById: async (id: number): Promise<PurchaseWithProduct | null> => {
    try {
      const { data, error } = await supabase
        .from('purchases')
        .select(`
          *,
          products:product_id (*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener compra por ID:', error);
        return null;
      }

      const product = data.products;
      return convertSupabasePurchaseWithProduct(data, product);
    } catch (error) {
      console.error('Error inesperado al obtener compra por ID:', error);
      return null;
    }
  },

  // Agregar una nueva compra
  addPurchase: async (purchaseData: PurchaseFormData): Promise<Purchase | null> => {
    try {
      // Calcular costos
      const totalCost = (purchaseData.unit_cost * purchaseData.quantity) + purchaseData.shipping_cost + purchaseData.other_expenses;
      const finalUnitCost = totalCost / purchaseData.quantity;

      
      // Subir documento de referencia si existe y es un archivo File
      let referenceDocumentUrl = '';
      if (purchaseData.reference_document && purchaseData.reference_document instanceof File) {
        try {
          const uploadResult = await uploadImage(purchaseData.reference_document, {
            folder: 'atomic-corporation/purchases',
            resource_type: 'auto'
          });

          referenceDocumentUrl = uploadResult.secure_url;
          console.log('✅ Documento subido exitosamente a Cloudinary:', uploadResult.secure_url);
        } catch (uploadError) {
          console.error('Error al subir documento de referencia:', uploadError);
          // Continuar sin el documento si hay error en la subida
        }
      }
      const { data, error } = await supabase
        .from('purchases')
        .insert([{
          product_id: purchaseData.product_id,
          quantity: purchaseData.quantity,
          unit_cost: purchaseData.unit_cost,
          shipping_cost: purchaseData.shipping_cost,
          other_expenses: purchaseData.other_expenses,
          total_cost: totalCost,
          final_unit_cost: finalUnitCost,
          reference_document: purchaseData.reference_document?.name,
          reference_document_url: referenceDocumentUrl,
          purchase_date: purchaseData.purchase_date
        }])
        .select()
        .single();

      if (error) {
        console.error('Error al agregar compra:', error);
        return null;
      }

      // Actualizar el stock del producto
      await updateProductStock(purchaseData.product_id, purchaseData.quantity);

      return convertSupabasePurchase(data);
    } catch (error) {
      console.error('Error inesperado al agregar compra:', error);
      return null;
    }
  },

  // Actualizar una compra existente
  updatePurchase: async (id: number, updatedPurchase: Partial<PurchaseFormData>): Promise<Purchase | null> => {
    try {
      const updateData: any = {};
      
      if (updatedPurchase.product_id !== undefined) updateData.product_id = updatedPurchase.product_id;
      if (updatedPurchase.quantity !== undefined) updateData.quantity = updatedPurchase.quantity;
      if (updatedPurchase.unit_cost !== undefined) updateData.unit_cost = updatedPurchase.unit_cost;
      if (updatedPurchase.shipping_cost !== undefined) updateData.shipping_cost = updatedPurchase.shipping_cost;
      if (updatedPurchase.other_expenses !== undefined) updateData.other_expenses = updatedPurchase.other_expenses;
      if (updatedPurchase.purchase_date !== undefined) updateData.purchase_date = updatedPurchase.purchase_date;

      // Recalcular costos si se modificaron los valores relevantes
      if (updatedPurchase.quantity !== undefined ||
          updatedPurchase.unit_cost !== undefined ||
          updatedPurchase.shipping_cost !== undefined ||
          updatedPurchase.other_expenses !== undefined) {
        
        // Obtener la compra actual para los valores que no se actualizan
        const currentPurchase = await purchaseService.getPurchaseById(id);
        if (currentPurchase) {
          const quantity = updatedPurchase.quantity ?? currentPurchase.quantity;
          const unitCost = updatedPurchase.unit_cost ?? currentPurchase.unit_cost;
          const shippingCost = updatedPurchase.shipping_cost ?? currentPurchase.shipping_cost;
          const otherExpenses = updatedPurchase.other_expenses ?? currentPurchase.other_expenses;
          
          const totalCost = (unitCost * quantity) + shippingCost + otherExpenses;
          const finalUnitCost = totalCost / quantity;
          
          updateData.total_cost = totalCost;
          updateData.final_unit_cost = finalUnitCost;
        }
      }

      // Subir nuevo documento de referencia solo si se proporciona un nuevo archivo File
      // No subir si reference_document es undefined o null (significa que no se cambió el documento)
      if (updatedPurchase.reference_document && updatedPurchase.reference_document instanceof File) {
        try {
          console.log('🔄 Subiendo nuevo documento para compra actualizada...');
          const uploadResult = await uploadImage(updatedPurchase.reference_document, {
            folder: 'atomic-corporation/purchases',
            resource_type: 'auto'
          });
          updateData.reference_document = updatedPurchase.reference_document.name;
          updateData.reference_document_url = uploadResult.secure_url;
          console.log('✅ Nuevo documento subido exitosamente:', uploadResult.secure_url);
        } catch (uploadError) {
          console.error('Error al subir documento de referencia:', uploadError);
        }
      }

      const { data, error } = await supabase
        .from('purchases')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar compra:', error);
        return null;
      }

      return convertSupabasePurchase(data);
    } catch (error) {
      console.error('Error inesperado al actualizar compra:', error);
      return null;
    }
  },

  // Eliminar una compra
  deletePurchase: async (id: number): Promise<boolean> => {
    try {
      // Obtener la compra antes de eliminarla para actualizar el stock
      const purchase = await purchaseService.getPurchaseById(id);
      if (!purchase) {
        return false;
      }

      const { error } = await supabase
        .from('purchases')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error al eliminar compra:', error);
        return false;
      }

      // Restar el stock del producto
      await updateProductStock(purchase.product_id, -purchase.quantity);

      return true;
    } catch (error) {
      console.error('Error inesperado al eliminar compra:', error);
      return false;
    }
  },

  // Obtener compras por producto
  getPurchasesByProduct: async (productId: number): Promise<Purchase[]> => {
    try {
      const { data, error } = await supabase
        .from('purchases')
        .select('*')
        .eq('product_id', productId)
        .order('purchase_date', { ascending: false });

      if (error) {
        console.error('Error al obtener compras por producto:', error);
        return [];
      }

      return data.map(convertSupabasePurchase);
    } catch (error) {
      console.error('Error inesperado al obtener compras por producto:', error);
      return [];
    }
  }
};

// Función auxiliar para actualizar el stock de un producto
const updateProductStock = async (productId: number, quantityToAdd: number): Promise<void> => {
  try {
    // Obtener el stock actual del producto
    const { data: product, error: productError } = await supabase
      .from('products')
      .select('stock')
      .eq('id', productId)
      .single();

    if (productError) {
      console.error('Error al obtener stock del producto:', productError);
      return;
    }

    // Calcular nuevo stock
    const currentStock = product.stock || 0;
    const newStock = Math.max(0, currentStock + quantityToAdd);

    // Actualizar stock y estado inStock
    const { error: updateError } = await supabase
      .from('products')
      .update({
        stock: newStock,
        in_stock: newStock > 0
      })
      .eq('id', productId);

    if (updateError) {
      console.error('Error al actualizar stock del producto:', updateError);
    }
  } catch (error) {
    console.error('Error inesperado al actualizar stock:', error);
  }
};